import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { EyeOff, Lock, X } from 'lucide-react';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ContourEditBlocker } from '~/domain/dicom/contourEditBlocker';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { dicomColorToRgbString } from '~/utils/color';

type StructureChipProps = {
    roi: RoiReadModel;
    testId: string;
    isHidden?: boolean;
    /**
     * Why this structure cannot be deleted; the tooltip names the guard so
     * the user knows the remedy. Domain vocabulary — precedence is decided
     * by resolveContourEditBlocker, not here.
     */
    undeletableReason?: ContourEditBlocker;
    onRemove?: (roiNumber: number) => void;
};

/**
 * Compact structure pill: color swatch + name with state badges. Rendered
 * with span elements only so it stays valid inside dialog text paragraphs.
 */
export default function StructureChip({
    roi,
    testId,
    isHidden = false,
    undeletableReason,
    onRemove,
}: StructureChipProps) {
    const intl = useIntl();

    const tooltip: ReactNode = (
        <>
            {roi.name}
            {isHidden && (
                <Typography
                    component="span"
                    display="block"
                    sx={{ fontSize: 'inherit' }}
                >
                    <FormattedMessage id="structure.bulk.chipHidden" />
                </Typography>
            )}
            {undeletableReason && (
                <Typography
                    component="span"
                    display="block"
                    sx={{ fontSize: 'inherit' }}
                >
                    <FormattedMessage
                        id={
                            undeletableReason === 'lock'
                                ? 'structure.bulk.chipLocked'
                                : 'structure.bulk.chipApproved'
                        }
                    />
                </Typography>
            )}
        </>
    );

    return (
        <Tooltip title={tooltip} placement="top-start">
            <Box
                component="span"
                data-testid={testId}
                display="inline-flex"
                alignItems="center"
                gap={0.5}
                pl={0.75}
                pr={onRemove ? 0.25 : 0.75}
                py={0.25}
                maxWidth="100%"
                sx={{
                    borderRadius: 1,
                    bgcolor: 'action.selected',
                    opacity: isHidden ? 0.65 : 1,
                }}
            >
                <Box
                    component="span"
                    flexShrink={0}
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: dicomColorToRgbString(roi.color),
                    }}
                />
                <Typography
                    component="span"
                    noWrap
                    sx={{ fontSize: 11, maxWidth: 110 }}
                >
                    {roi.name}
                </Typography>
                {isHidden && (
                    <EyeOff
                        size={10}
                        aria-hidden="true"
                        data-testid="bulk-chip-hidden-badge"
                    />
                )}
                {undeletableReason && (
                    <Lock
                        size={10}
                        aria-hidden="true"
                        data-testid="bulk-chip-locked-badge"
                    />
                )}
                {onRemove && (
                    <IconButton
                        size="small"
                        sx={{ p: 0.25 }}
                        onClick={() => onRemove(roi.roiNumber)}
                        data-testid={`${testId}-remove`}
                        aria-label={intl.formatMessage(
                            { id: 'structure.bulk.removeFromSelection' },
                            { name: roi.name }
                        )}
                    >
                        <X size={10} />
                    </IconButton>
                )}
            </Box>
        </Tooltip>
    );
}
